/**
 * List of elements that are focusable.
 */
const focusableElementsList = [
	'a[href]', // links
	'button', // buttons
	'input:not([type="hidden"])', // any input that is not hidden (text, checkboxes, etc.)
	'select', // dropdowns
	'summary', // details/summary elements
	'[tabindex]:not([tabindex^="-"])', // elements that have a tabindex that is not -1
	'textarea', // textareas
]

const reasonsNotToSelect = [
	'[hidden]', // elements that are hidden
	'[disabled]', // elements that are disabled
	'[inert]', // elements that are inert
	'[style*="display:none" i]', // elements that have a style of display: (case insensitive)
	'[style*="display: none" i]', // these two selectors are not perfect but better than nothing
	'[popover]:not(:popover-open)', // popovers that are not open
]

/**
 * Add a focus trap to the element with the selector passed in as the argument.
 * @param element: {HTMLElement} - The element to add the focus trap to.
 * @param selector: {string} - The selector to limit the focus trap to (within the element).
 * @return {function} - The function to handle the focus trap (should be assigned to the keydown event handler)
 */
export function focusTrap(element: HTMLElement, selector?: string): (e: KeyboardEvent) => void {
	const elements = focusableElementsList.join(', ')
	const reasons = reasonsNotToSelect.join(', ')
	return function (e: KeyboardEvent): void {
		if (e.key === 'Tab') {
			const parsedSelector = selector ? `:is(${selector})` : ''
			const focusableElements = element.querySelectorAll(
				`${parsedSelector} :is(${elements}):not(:is(${reasons})):not(:is(${reasons}) :is(${elements}))`
			)
			const visibleFocusableElements = Array.from(focusableElements).filter(
				element => element.checkVisibility?.() ?? true
			)
			const firstFocusableElement = visibleFocusableElements[0]
			const lastFocusableElement = visibleFocusableElements[visibleFocusableElements.length - 1]

			if (visibleFocusableElements.length > 0) {
				if (e.shiftKey && e.target === firstFocusableElement) {
					e.preventDefault()
					;(lastFocusableElement as HTMLElement).focus()
				} else if (!e.shiftKey && e.target === lastFocusableElement) {
					e.preventDefault()
					;(firstFocusableElement as HTMLElement).focus()
				}
			}
		}
	}
}
